import type { KeyboardEventHandler } from 'react';
import { useCallback, useContext, useState } from 'react';
import styled from '@emotion/styled';
import { ChatActionType, type RepSearchResult } from '@trinity/chat';
import { useSelectedUserTerritoryId } from '@trinity/chat-components/components/Chat.Filters/hooks/useSelectedUserTerritoryId';
import { SelectRep } from '@trinity/chat-components/components/Search/Rep';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { SearchQueryHint } from '../presentation/Message.QueryHint';
import { SubmitActionContext } from './context';
import { useRepSelectionAction } from './hooks/useRepSelectionAction';
import { BasicSystemUserMessage } from './Message.System.Basic';
import { MessageContainer } from './Message.System.Wrapper';

type Props = {
  disabled?: boolean;
  searchRepsQueryFn?: (params: {
    name: string;
    brandIdentifier: string | null;
    territoryId?: string;
  }) => Promise<RepSearchResult[]>;
};

export const RepSearchResponse = ({ disabled = false, searchRepsQueryFn }: Props) => {
  const submitAction = useContext(SubmitActionContext);
  const submitRepSelection = useRepSelectionAction();
  const apiClient = useChatApiClient();
  const territoryId = useSelectedUserTerritoryId();

  const [inputValue, setInputValue] = useState('');
  const [selectedRep, setSelectedRep] = useState<RepSearchResult | null>(null);

  const queryFn = useCallback(
    async ({
      name,
      brandIdentifier,
      territoryId: selectedTerritoryId,
    }: {
      name: string;
      brandIdentifier: string | null;
      territoryId?: string;
    }) => {
      if (searchRepsQueryFn) {
        return searchRepsQueryFn({ name, brandIdentifier, territoryId: selectedTerritoryId });
      }

      const res = await apiClient.search.searchReps({
        name,
        territoryId: selectedTerritoryId,
      });

      return res.results;
    },
    [apiClient, searchRepsQueryFn]
  );

  const handleInputChange = useCallback((value: string) => {
    setInputValue(value);
    setSelectedRep(null);
  }, []);

  const handleSelect = useCallback(
    (rep: RepSearchResult) => {
      if (disabled) return;

      setSelectedRep(rep);
      setInputValue(rep.name);

      submitRepSelection(rep);
    },
    [disabled, submitRepSelection]
  );

  const handleSubmit = useCallback(() => {
    if (disabled) return;

    if (selectedRep) {
      submitRepSelection(selectedRep);
      return;
    }

    const trimmedName = inputValue.trim();
    if (trimmedName.length === 0) return;

    submitAction(
      {
        type: ChatActionType.SearchReps,
        payload: {
          name: trimmedName,
          territoryId,
        },
      },
      `Find any matching Rep with the name ${trimmedName}`
    );
  }, [disabled, inputValue, selectedRep, submitAction, submitRepSelection, territoryId]);

  const handleKeyDown: KeyboardEventHandler<HTMLInputElement> = useCallback(
    (e) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  return (
    <MessageContainer>
      <BasicSystemUserMessage>
        <Body>
          <IntroText>
            Get started with a search to pull a Rep Profile
            <br />
            First let&apos;s find the Rep you&apos;re looking for.
          </IntroText>

          <FormRow>
            <Label>Name</Label>
            <SelectRep
              disabled={disabled}
              inputValue={inputValue}
              selectedRep={selectedRep}
              onInputChange={handleInputChange}
              onSelection={handleSelect}
              onKeyDown={handleKeyDown}
              territoryId={territoryId}
              queryFn={queryFn}
            />
          </FormRow>

          <SearchQueryHint
            disabled={disabled || inputValue.trim().length === 0}
            onClick={handleSubmit}
            value="Search for profiles."
          />
        </Body>
      </BasicSystemUserMessage>
    </MessageContainer>
  );
};

const Body = styled.div({
  width: '100%',
});

const IntroText = styled.div(({ theme }) => ({
  marginBottom: 16,
  fontFamily: theme.fonts.regular,
}));

const FormRow = styled.div({
  display: 'flex',
  alignItems: 'center',
  gap: 12,
  marginBottom: 16,
});

const Label = styled.span(({ theme }) => ({
  fontFamily: theme.fonts.semiBold,
  fontSize: 14,
  minWidth: 50,
}));
